import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { UserForm } from '../../modules/forms/models/UserForm'
import { navigate } from '@reach/router'
import { ROUTE_FORM_GENERATOR } from '../../routes/routes-constants'
import {
  FORM_SERVICE_KEY,
  IFormService,
  IUserFormService,
  USER_FORM_SERVICE_KEY,
} from '../../modules/forms'
import { getFormContainer } from '../../container/form-module'
import { Query, QueryParam } from '../../common/api/Query'
import style from '../../components/loading-spinner/LoadingSpinner.module.css'
import styles from './TableResults.module.css'
import { LoadingSpinner } from 'components/loading-spinner/LoadingSpinner'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import genericStyle from '../../common/utils/generic.module.css'
import { emptyFormDTO, FormDTO, fromModel } from '../../modules/forms/models/FormDTO'
import { getUserContainer } from 'container/user-module'
import { IUserService } from 'modules/users/services/UserService'
import { USER_SERVICE_KEY } from 'modules/users'
import { User } from 'modules/users/models/User'
import { Pagination } from '../../components/table/Pagination'
import { AppTable } from '../../components/table/AppTable'
import { Field } from '../../components/table/types'
import { UserFormValue } from 'modules/forms/models/UserFormValue'
import { FormFieldType } from '../../modules/forms/enums/FormFieldType'

type FormGeneratorResultsProps = {
  id?: string
}

type TableRowData = {
  id: string
  userName: string
  submissionDate: string
  formValues: { [key: string]: string }
  userFormId: string
  [key: string]: any
}

const formContainer = getFormContainer()
const formService = formContainer.get<IFormService>(FORM_SERVICE_KEY)
const userFormService = formContainer.get<IUserFormService>(USER_FORM_SERVICE_KEY)
const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)

export function TableResults(props: FormGeneratorResultsProps) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [count, setCount] = useState<number>(0)
  const [form, setForm] = useState<FormDTO>(emptyFormDTO())
  const [tableData, setTableData] = useState<TableRowData[]>([])
  const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map())
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [hasResponses, setHasResponses] = useState<boolean>(true)

  useEffect(() => {
    if (props.id) {
      formService.getByID(props.id).subscribe((res) => {
        if (res) {
          setForm(fromModel(res))
        }
      })
    }
  }, [props.id])

  useEffect(() => {
    if (!props.id) return

    setIsLoading(true)

    userFormService
      .getFilteredList(
        new Query({
          pager: { offset: page * rowsPerPage, limit: rowsPerPage },
          query: [new QueryParam<UserForm>('formID', props.id)],
          sort: [{ field: 'form_id' }],
        })
      )
      .subscribe((res) => {
        setHasResponses(res.items.length > 0)

        const userIdsSet = new Set<string>()
        res.items.forEach((item) => userIdsSet.add(item.userID))
        const userIds = Array.from(userIdsSet)

        if (userIds.length > 0) {
          userService
            .getFilteredList(
              new Query({
                query: [{ name: 'ids', value: userIds }],
              })
            )
            .subscribe((userRes) => {
              if (userRes) {
                const newUsersMap = new Map<string, User>()
                userRes.items.forEach((user) => {
                  newUsersMap.set(user.id, user)
                })
                setUsersMap(newUsersMap)
              }
            })
        }

        const processedData: TableRowData[] = []

        res.items.forEach((userForm) => {
          const dateGroups = new Map<string, UserFormValue[]>()

          userForm.userFormValues.forEach((formValue) => {
            const dateKey = formValue.date.toString()
            if (!dateGroups.has(dateKey)) {
              dateGroups.set(dateKey, [])
            }
            dateGroups.get(dateKey)!.push(formValue)
          })

          dateGroups.forEach((values, dateKey) => {
            const formValues: { [key: string]: string } = {}

            values.forEach((formValue) => {
              const fieldId = formValue.formFieldID
              const vals = formValue.getValues()

              if (vals && Array.isArray(vals) && vals.length > 0) {
                formValues[fieldId] = vals.join(', ')
              } else {
                formValues[fieldId] = t('notComplete')
              }
            })

            processedData.push({
              id: `${userForm.id || ''}-${dateKey}`,
              userName: userForm.userID,
              submissionDate: dateKey,
              formValues: formValues,
              userFormId: userForm.id || '',
            })
          })
        })

        setTableData(processedData)
        setCount(res.count)

        setTimeout(() => {
          setIsLoading(false)
        }, 300)
      })
  }, [props.id, page, rowsPerPage, t])

  const formatDateValue = (value: string, fieldType?: string | number): string => {
    const type = typeof fieldType === 'string' ? parseInt(fieldType) : fieldType

    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
    if (isoDateRegex.test(value)) {
      const date = new Date(value)

      if (type === FormFieldType.Time) {
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
      }

      if (type === FormFieldType.Date) {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
      }

      if (type === FormFieldType.DateTime) {
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year} ${hours}:${minutes}`
      }

      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year} ${hours}:${minutes}`
    }

    const dateObj = new Date(value)
    if (!isNaN(dateObj.getTime())) {
      if (type === FormFieldType.Time) {
        const hours = dateObj.getHours().toString().padStart(2, '0')
        const minutes = dateObj.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
      }

      if (type === FormFieldType.Date) {
        const day = dateObj.getDate().toString().padStart(2, '0')
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
        const year = dateObj.getFullYear()
        return `${day}/${month}/${year}`
      }

      if (type === FormFieldType.DateTime) {
        const hours = dateObj.getHours().toString().padStart(2, '0')
        const minutes = dateObj.getMinutes().toString().padStart(2, '0')
        const day = dateObj.getDate().toString().padStart(2, '0')
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
        const year = dateObj.getFullYear()
        return `${day}/${month}/${year} ${hours}:${minutes}`
      }

      const hours = dateObj.getHours().toString().padStart(2, '0')
      const minutes = dateObj.getMinutes().toString().padStart(2, '0')
      const day = dateObj.getDate().toString().padStart(2, '0')
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
      const year = dateObj.getFullYear()
      return `${day}/${month}/${year} ${hours}:${minutes}`
    }

    const simpleDateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (simpleDateRegex.test(value)) {
      if (type === FormFieldType.Time) {
        return value
      }
      const [year, month, day] = value.split('-')
      return `${day}/${month}/${year}`
    }

    const weekdayMonthRegex = /^\w{3} \w{3} \d{1,2} \d{4}$/
    if (weekdayMonthRegex.test(value)) {
      const dateObj = new Date(value)
      if (!isNaN(dateObj.getTime())) {
        if (type === FormFieldType.Date) {
          const day = dateObj.getDate().toString().padStart(2, '0')
          const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
          const year = dateObj.getFullYear()
          return `${day}/${month}/${year}`
        }
        if (type === FormFieldType.DateTime) {
          const hours = dateObj.getHours().toString().padStart(2, '0')
          const minutes = dateObj.getMinutes().toString().padStart(2, '0')
          const day = dateObj.getDate().toString().padStart(2, '0')
          const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
          const year = dateObj.getFullYear()
          return `${day}/${month}/${year} ${hours}:${minutes}`
        }
        const day = dateObj.getDate().toString().padStart(2, '0')
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
        const year = dateObj.getFullYear()
        return `${day}/${month}/${year}`
      }
    }

    return value
  }

  const renderValue = (value: string, formField?: any) => {
    if (value === 'true') return t('yes')
    if (value === 'false') return t('no')

    if (formField?.type !== undefined) {
      return formatDateValue(value, formField.type)
    }

    const dateFormats = [
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/,
      /^\d{4}-\d{2}-\d{2}$/,
      /\w{3} \w{3} \d{2} \d{4}/,
    ]

    const isDate = dateFormats.some((format) => format.test(value))
    if (isDate) {
      return formatDateValue(value)
    }

    const translatedValue = t(value)
    return translatedValue !== value ? translatedValue : value
  }

  const fields: Field<TableRowData>[] = [
    {
      name: 'userName',
      label: t('username'),
      sortable: false,
      renderFunc: (field, item) => {
        const user = usersMap.get(item.userName)
        return user ? user.firstName : item.userName
      },
    },
    {
      name: 'submissionDate',
      label: t('date'),
      sortable: false,
      renderFunc: (field, item) => {
        return new Date(item.submissionDate).toLocaleDateString('es-ES')
      },
    },
    ...form.formFields.map((formField) => ({
      name: formField.id as keyof TableRowData,
      label: formField.title ? t(formField.title) : '',
      sortable: false,
      renderFunc: (field: any, item: TableRowData) => {
        const value = item.formValues[formField.id]
        return value ? renderValue(value, formField) : '-'
      },
    })),
  ]

  const handlePaginationChange = (e: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value)
    if (!Number.isNaN(value)) {
      setRowsPerPage(value)
      setPage(0)
    }
  }

  const goBack = () => navigate(ROUTE_FORM_GENERATOR)

  return (
    <Box mb={3} className={genericStyle.pageContainer}>
      {!isLoading ? (
        <Box className={styles.container}>
          <Box mb={3}>
            <AppButton
              theme={ButtonTheme.NewSecondary}
              type={'button'}
              label={t('back')}
              handler={goBack}
            />
          </Box>

          {!hasResponses ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
              style={{
                padding: '40px',
                color: '#666',
                fontStyle: 'italic',
                fontSize: '16px',
              }}
            >
              {t('noResponsesYet')}
            </Box>
          ) : (
            <>
              <AppTable fields={fields} items={tableData} rowKeyField="id" isLoading={isLoading} />

              {count > 0 && (
                <Box mt={3}>
                  <Pagination
                    page={page}
                    count={count}
                    handleChangePage={handlePaginationChange}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      ) : (
        <LoadingSpinner className={style.loadingSpinner} />
      )}
    </Box>
  )
}
